/**
 * 接続管理ページ群 (Amazon Seller / Amazon Ads / Google Ads / ...) で
 * 新規接続フォームを表示するための共通モーダルシェル。
 *
 * 各プラットフォームは固有のフォーム内容を ``children`` として渡し、
 * モーダルの開閉・タイトル・閉じるボタン・サイズはこちらが提供する。
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './NewConnectionModal.module.css';

interface Props {
  isOpen: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
}

export function NewConnectionModal({ isOpen, title, description, onClose, children }: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
        {description && <p className={styles.description}>{description}</p>}
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
